import { Meteor } from 'meteor/meteor';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Accounts } from 'meteor/accounts-base';
import { strings } from '/imports/strings';
import { ErrorCode } from '/imports/errors';
import { defer, isMobile, isValidEmail, sleep } from '/imports/utils';
import { setLocalStorage } from '/imports/localStorage';
import { Button } from '/imports/ui/Button';
import { Input } from '/imports/ui/Input';
import { PublicFooter } from '/imports/ui/PublicFooter';
import { Loading } from '/imports/ui/Loading';
import { SignedOutPageStyled } from '/imports/ui/SignedOutPage';
import { Logo } from '/imports/ui/Logo';
import { toastOnError } from '/imports/ui/utils';
import { LoadingFullScreen } from '/imports/ui/LoadingFullScreen';
import { sendLoginLink } from '/imports/api/users';

const loginPageClasses = [
  '[&_.logo]:max-w-full [&_.logo_img]:w-full',
  '[&_.links]:relative',
  '[&_.magic-link]:absolute [&_.magic-link]:left-0',
  '[&_.forgot-password]:absolute [&_.forgot-password]:right-0',
  '[&_.signup]:mt-8 [&_.signup]:hidden [&_.signup]:text-center',
  '[&_.signup_p]:m-0',
  '[&_.signup_.links]:flex [&_.signup_.links]:justify-center [&_.signup_.links]:gap-10',
  '[&_.inputs_label]:mt-4',
  '[&_.terms]:mt-8',
].join(' ');

export const LoginPageStyled = (props: React.ComponentProps<typeof SignedOutPageStyled>) => (
  <SignedOutPageStyled
    {...props}
    className={`${loginPageClasses}${props.className ? ` ${props.className}` : ''}`}
  />
);

const defaultRedirect = '/';

export const LoginPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const redirect = window.atob(params.get('r') || '') || defaultRedirect;
  const [jwt, setJwt] = useState(params.get('jwt'));
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [enableLink, setEnableLink] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  useEffect(() => { setLocalStorage('returnVisitor', true); }, []);
  useEffect(() => {
    isValidEmail(username) ? setEnableLink(false) : setEnableLink(false);
  }, [username]);
  useEffect(() => {
    if (!jwt) return;
    Accounts.callLoginMethod({
      methodArguments: [{ jwt }], userCallback(error) {
        if (error) {
          toast.error(strings.InvalidOrExpiredVerificationToken());
          setLoggingIn(false);
          setJwt('');
        }
        else {
          navigate('/');
        }
      },
    });
  }, [jwt]);
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setLoggingIn(true);
    setError('');
    if (username && password) login();
    if (username && !password) magicLink();
  };
  const login = () => {
    Meteor.loginWithPassword({ username }, password, (error: any) => {
      setLoggingIn(false);
      if (error?.error === 403) {
        setError(strings.InvalidUsernameOrPassword());
        return false;
      }
      else if (error?.error === ErrorCode.unauthorized) {
        setError(strings.InvalidUsernameOrPassword());
        return false;
      }
      else if (error?.error === ErrorCode.inactiveUserLogin) {
        setError(strings.AccountIsDeactivated());
        return false;
      }
      else if (error?.error === ErrorCode.unverifiedUserLogin) {
        setError(strings.PleaseVerifyYourEmailAddressBeforeLoggingIn());
        return false;
      }
      else if (error) {
        console.log(error);
      }
      else {
        setLoggingIn(true);
        defer(async () => {
          let user;
          while (!user) {
            user = await Meteor.userAsync();
            if (user) window.location.href = user?.settings?.clientPath || redirect?.includes('logout') ? defaultRedirect : redirect;
            else await sleep(250);
          }
        });
      }
    });
  };
  const magicLink = toastOnError(async () => {
    const email = await sendLoginLink.call(username);
    setLoggingIn(false);
    toast.success(strings.LinkSentPleaseCheckYourInbox(email));
  });
  if (jwt) return <LoadingFullScreen />;
  return (
    <LoginPageStyled className="page">
      <div className="wrapper">
        <div className="inner-wrapper">
          <div><Logo size={isMobile() ? 3 : 5} /></div>
          <div>
            <div>{strings.PleaseEnterYourLoginDetailsBelow()}</div>
          </div>
          <form onSubmit={onSubmit}>
            <div className="inputs">
              <label>{strings.Email()}</label>
              <Input inputId="login-username" placeholder="mail@website.com" value={username} autoFocus={true}
                onChange={(username) => setUsername(username || '')} />
              <label>{strings.Password()}</label>
              <Input inputId="login-password" type="password" placeholder="••••••••••••" value={password}
                onChange={(password) => setPassword(password || '')} autoComplete='off' onFocus={(e) => e.target.removeAttribute('readonly')} />
              {error && <div className="form-error">{error}</div>}
              <div className="links">
                <Link className="forgot-password" to='/forgot-password'>{strings.ForgotPassword()}</Link>
                {enableLink && <Link className="magic-link" to='#' onClick={() => magicLink()}>{strings.SendLoginLink()}</Link>}
              </div>
            </div>
            <div className="buttons">
              <Button
                id="login-submit" type="submit"
                className={`btn ${loggingIn ? 'tertiary' : 'primary'}`}
                disabled={!username}
              >
                {
                  loggingIn ?
                    <Loading />
                    :
                    <div className="button-label">{username && !password ? strings.SendLoginLink() : strings.SignIn()}</div>
                }
              </Button>
              <div className="signup">
                <p>{strings.DontHaveAnAccount()}</p>
                <p className="links"><Link to="/signup">{strings.MakeOneHere()}</Link></p>
              </div>
            </div>
            <div className="terms">
              You acknowledge that by clicking &quot;Sign In&quot;<br />you are agreeing to our<br/>
              <a href="/terms" target="_blank" rel="noreferrer">Terms of Service</a> and <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>
            </div>
          </form>
        </div>
      </div>
      <PublicFooter />
    </LoginPageStyled>
  );
};